import './App.css'

import {ApolloProvider} from '@apollo/client'
import {StatusBar} from '@capacitor/status-bar'
import {createInstance, MatomoProvider} from '@datapunt/matomo-tracker-react'
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles'
import {setUser as setSentryUser, withSentryReactRouterV6Routing} from '@sentry/react'
import {SafeArea} from 'capacitor-plugin-safe-area'
import {ReactNode, useContext, useEffect, useMemo, useState} from 'react'
import {
  Admin,
  CustomRoutes as RaCustomRoutes,
  Loading,
  memoryStore,
  Resource,
  useGetIdentity,
} from 'react-admin'
import {BrowserRouter, Navigate, Route} from 'react-router-dom'

import AppFavicon from './components/AppFavicon'
import AppLanguageSetup from './components/AppLanguageSetup'
import AppUpdateAvailableDialog from './components/AppUpdateAvailableDialog'
import Center from './components/Center'
import ExtendedNotification from './components/ExtendedNotification'
import PushNotificationsHandler from './components/PushNotificationsHandler'
import ApiUrlsContext, {ApiUrlsProvider} from './contexts/ApiUrlsContext'
import {BrowserNotificationsProvider} from './contexts/BrowserNotificationsContext'
import {HistoryStackProvider} from './contexts/HistoryStackContext'
import {MobileKeyboardProvider} from './contexts/MobileKeyboardContext'
import SandboxProvider from './contexts/SandboxContext'
import useAuthWatcher from './hooks/useAuthWatcher'
import useBaseHostname from './hooks/useBaseHostname'
import useDeepLinkHandler from './hooks/useDeepLinkHandler'
import useGetAppUpdateInfo from './hooks/useGetAppUpdateInfo'
import useHasPermission from './hooks/useHasPermission'
import useMobileAppBadge from './hooks/useMobileAppBadge'
import useSessionMerchantUser from './hooks/useSessionMerchantUser'
import Layout from './Layout'
import CompanyDetailsPage from './pages/CompanyDetailsPage'
import CompanySettingsPage from './pages/CompanySettingsPage'
import ExternalChatPage from './pages/ExternalChatPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import InternalChatPage from './pages/InternalChatPage/index'
import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import {CampaignCreate, CampaignShow, CampaignsList} from './resources/campaignsResource'
import {
  ChatMessageTemplateCreate,
  ChatMessageTemplateShow,
  ChatMessageTemplatesList,
} from './resources/chatMessageTemplatesResource'
import {
  MerchantCustomerUserCreate,
  MerchantCustomerUsersEdit,
  MerchantCustomerUsersList,
} from './resources/merchantCustomerUsersResource'
import {MerchantUserEdit} from './resources/merchantUsersResource'
import {TagCreate, TagEdit} from './resources/tagsResource'
import theme from './theme'
import authProvider from './utils/authProvider'
import buildDataProvider, {_apolloClient} from './utils/dataProvider'
import {isEmbedded} from './utils/embedded'
import env from './utils/env'
import i18nProvider, {DEFAULT_LANGUAGE, MESSAGES} from './utils/i18nProvider'
import {isNativeAndroidApp, isNativeMobileApp} from './utils/platform'

const App = () => {
  const baseHostname = useBaseHostname()
  const {isAppUpdateAvailable, isImmediateUpdateAllowed} = useGetAppUpdateInfo()
  useEffect(() => {
    if (!isNativeMobileApp) return
    (async () => {
      if (isNativeAndroidApp) {
        await StatusBar.setOverlaysWebView({overlay: true})
      }
      const {insets: {bottom, top}} = await SafeArea.getSafeAreaInsets()
      document.documentElement.style.setProperty('--safe-area-top-inset', `${top}px`)
      document.documentElement.style.setProperty(
        '--safe-area-bottom-inset', bottom ? `${bottom}px` : 'unset',
      )
    })()
  })
  return (
    <ApiUrlsProvider baseHostname={baseHostname}>
      <BrowserRouter>
        <HistoryStackProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              {(isNativeMobileApp && isAppUpdateAvailable) ? (
                <AppUpdateAvailableDialog
                  isImmediateUpdateAllowed={isImmediateUpdateAllowed}
                />
              ) : (
                <AppResources />
              )}
            </ThemeProvider>
          </StyledEngineProvider>
        </HistoryStackProvider>
      </BrowserRouter>
    </ApiUrlsProvider>
  )
}

const AppResources = () => {
  const [dataProvider, setDataProvider] = useState<any>(null)
  const {graphqlApiUrl, graphqlWebsocketApiUrl} = useContext(ApiUrlsContext)
  useDeepLinkHandler()
  useEffect(() => {
    const _buildDataProvider = async () => {
      await authProvider.checkAuth({}).catch(() => {})
      const dataProvider = await buildDataProvider(
        {
          getToken: () => authProvider.getToken(),
          uri: graphqlApiUrl,
          websocketUri: graphqlWebsocketApiUrl,
        },
      )
      setDataProvider(() => dataProvider)
    }
    _buildDataProvider()
  }, [graphqlApiUrl, graphqlWebsocketApiUrl])
  if (!dataProvider) {
    return (
      <Center>
        <Loading
          loadingPrimary={MESSAGES[DEFAULT_LANGUAGE].ra.page.loading}
          // @ts-ignore
          loadingSecondary={null}
        />
      </Center>
    )
  }
  return (
    <ApolloProvider client={_apolloClient}>
      <Admin
        authProvider={authProvider}
        dataProvider={dataProvider}
        catchAll={NotFoundPage}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        layout={Content}
        store={memoryStore()}
        notification={ExtendedNotification}
        theme={theme}
      >
        <Resource
          create={withPermissionsCheck(CampaignCreate, 'create', 'campaigns')}
          list={CampaignsList}
          name="campaigns"
          show={CampaignShow}
        />
        <Resource name="channels" />
        <Resource name="channel_chat_bot_users" />
        <Resource name="channel_merchant_users" />
        <Resource name="channel_working_hours" />
        <Resource name="contact_groups" />
        <Resource
          create={withPermissionsCheck(
            ChatMessageTemplateCreate, 'create', 'chat_message_templates'
          )}
          list={ChatMessageTemplatesList}
          name="chat_message_templates"
          show={ChatMessageTemplateShow}
        />
        <Resource name="customer_users" />
        <Resource name="file_mime_types" />
        <Resource
          create={withPermissionsCheck(
            MerchantCustomerUserCreate, 'create', 'merchant_customer_users'
          )}
          edit={MerchantCustomerUsersEdit}
          list={MerchantCustomerUsersList}
          name="merchant_customer_users"
        />
        <Resource name="merchant_customer_user_contact_groups" />
        <Resource name="merchant_customer_user_tags" />
        <Resource name="merchant_user_contact_groups" />
        <Resource
          name="merchant_users"
          edit={withPermissionsCheck(
            MerchantUserEdit, 'edit', 'merchant_users'
          )}
        />
        <Resource name="merchants" />
        <Resource name="organization_merchant_users" />
        <Resource name="secondary_responsible_merchant_users" />
        <Resource
          name="tags"
          create={withPermissionsCheck(
            TagCreate, 'edit', 'merchant_users'
          )}
          edit={withPermissionsCheck(
            TagEdit, 'edit', 'merchant_users'
          )}
        />
        <Resource name="users" />
        <Resource name="whatsapp_accounts" />
        <Resource name="whatsapp_message_templates" />
        <CustomRoutes>
          <Route key="home" element={<Navigate to="/inbox" />} path="/" />
          <Route element={<ExternalChatPage />} key="inbox" path="/inbox"/>
          <Route
            element={<ExternalChatPage />}
            key="inbox-channel"
            path="/inbox/:channelId"
          />
          <Route
            element={<ExternalChatPage />}
            key="inbox-chat"
            path="/inbox/:channelId/:chatId"
          />
          <Route element={<InternalChatPage />} key="internal-chat" path="/team-chat"/>
          <Route
            element={<InternalChatPage />}
            key="internal-chat"
            path="/team-chat/:chatId"
          />
          <Route element={<CompanySettingsPage />} path="/company-settings/"/>
        </CustomRoutes>
        <CustomRoutes noLayout>
          <Route
            element={<ForgotPasswordPage/>}
            key="forgot-password"
            path="/forgot-password"
          />
          <Route
            element={<ResetPasswordPage />}
            key="reset-password"
            path="/reset-password/:passwordResetCode"
          />
          <Route
            element={<CompanyDetailsPage/>}
            key="company-details"
            path="/company-details"
          />
        </CustomRoutes>
      </Admin>
    </ApolloProvider>
  )
}

const Content = ({children}: ContentProps) => {
  useAuthWatcher()
  useMobileAppBadge()
  return (
    <>
      <AppLanguageSetup />
      <AppFavicon />
      <TrackingSetup>
        <BrowserNotificationsProvider
          disabled={isEmbedded || isNativeMobileApp}
          shouldShowUserPrompt
        >
          <PushNotificationsHandler>
            <MobileKeyboardProvider>
              <SandboxProvider>
                <Layout>{children}</Layout>
              </SandboxProvider>
            </MobileKeyboardProvider>
          </PushNotificationsHandler>
        </BrowserNotificationsProvider>
      </TrackingSetup>
    </>
  )
}

const CustomRoutes = withSentryReactRouterV6Routing(RaCustomRoutes as any)

const withPermissionsCheck = (Component, action, resource) =>
  props => useHasPermission(action, resource) ?
    <Component {...props} /> :
    <NotFoundPage />

const TrackingSetup = ({children}) => {
  const {merchantUser: {emailAddress} = {}} = useSessionMerchantUser()
  const {identity: {id: userId, isImpersonatedUser} = {}} = useGetIdentity()
  const matomoUrl = useMemo(() => env('MATOMO_URL'), [])
  const matomoInstance = createInstance({
    configurations: {
      disableCookies: true,
    },
    disabled: !(
      userId &&
      matomoUrl &&
      // TODO: Uncomment next line
      // !emailAddress?.endsWith('@flinkit.de') &&
      emailAddress &&
      !isImpersonatedUser
    ),
    siteId: 1,
    urlBase: matomoUrl,
    userId: userId as string,
  })
  useEffect(() => {
    if (!userId) return
    setSentryUser({id: userId as string})
    return () => setSentryUser({id: undefined})
  }, [userId])
  return (
    // @ts-ignore
    <MatomoProvider value={matomoInstance}>
      {children}
    </MatomoProvider>
  )
}

interface ContentProps {
  children?: ReactNode
}

export default App
